import classNames from 'classnames'
import i18n, { t } from 'i18next'
import { Button } from 'primereact/button'
import { FileUpload } from 'primereact/fileupload'
import { InputTextarea } from 'primereact/inputtextarea'
import { Messages } from 'primereact/messages'
import { SelectButton } from 'primereact/selectbutton'
import { Toast } from 'primereact/toast'
import { Tree, TreeExpandedKeysType } from 'primereact/tree'
import TreeNode from 'primereact/treenode'
import React, { useEffect, useRef, useState } from 'react'
import { Controller, FieldError, FormProvider, useForm } from 'react-hook-form'
import { ObservationFileList } from './ObservationFileList'
import {
  API_OBSERVATION,
  API_OBSERVATION_DOWNLOAD,
  API_OBSERVATION_UPLOAD,
  API_OBSERVATIONS,
} from '../../../constants'
import { EducationRating } from '../../../model/assessment.model'
import { Rating, RequestMethod } from '../../../model/enums/enums'
import { Observation } from '../../../model/observation'
import { SignedUrlResponse } from '../../../model/signedUrlResponse.model'
import { State } from '../../../model/state.model'
import { useAppDispatch } from '../../../redux/hooks'

import {
  observationAdded,
  observationUpdated,
} from '../../../redux/official-document/education-report/observation/observationReducer'
import { handleRequest } from '../../../services/api/base/handleRequest'
import {
  findCompetenceNodeByKey,
  findCompetenceNodeByObservation,
  getCompetenceTree,
} from '../../../services/competenceService'
import { trimSpaces } from '../../../util/trimSpaces'
import { AllowPopupDialog } from '../../util/dialog/AllowPopupDialog'
import {
  getFormErrorMessage,
  getRequiredFormMessage,
} from '../../util/ErrorText'
import { ApprenticeDropdown } from '../../util/form/dropdown/person-dropdown/ApprenticeDropdown'
import {
  cancelOptions,
  chooseOptions,
  uploadOptions,
} from '../../util/form/MokaiUpload'
import { uploadFileTemplate } from '../../util/form/UploadFileTemplate'
import { headerTemplate } from '../../util/form/UploadHeader'

export const educationReportRating = (): EducationRating[] => {
  return [
    {
      value: 1,
      key: 'A',
      rating: Rating.requirementsExceeded,
      label: i18n.t<string>('core.rating.requirements_exceeded'),
    },
    {
      value: 2,
      key: 'B',
      rating: Rating.fulfilled,
      label: i18n.t<string>('core.rating.fulfilled'),
    },
    {
      value: 3,
      key: 'C',
      rating: Rating.barelyFulfilled,
      label: i18n.t<string>('core.rating.barely_fulfilled'),
    },
    {
      value: 4,
      key: 'D',
      rating: Rating.notFulfilled,
      label: i18n.t<string>('core.rating.not_fulfilled'),
    },
  ]
}

const defaultObservation: Observation = {
  created_on: undefined,
  hidden: undefined,
  id_apprentice: 0,
  id_competence_detail: undefined,
  id_reporter: 0,
  id_sub_competence: undefined,
  is_active: 1,
  last_change: undefined,
  observation_id: 0,
  rating: 0,
  reason: '',
  observation_file: [],
}

export const ObservationEntry = (props: {
  observation?: Observation
  handleEvent: () => void
}): JSX.Element => {
  const [showPopupBlocked, setShowPopupBlocked] = useState<boolean>(false)

  const toast = useRef<Toast>(null)
  const messages = useRef<Messages>(null)

  const [validDropArea, setValidDropArea] = useState<boolean>(false)

  const [selectedCompetence, setSelectedCompetenceKey] = useState<any>({})

  const [subCompetenceDbValue, setSubCompetenceDbValue] = useState<number>(0)
  const [subCompetenceDetailDbValue, setSubCompetenceDetailDbValue] =
    useState<number>(0)

  const defaultExpandedKeys = { '0': true }

  const [expandedKeys, setExpandedKeys] =
    useState<TreeExpandedKeysType>(defaultExpandedKeys)
  const [competenceTree, setCompetenceTree] = useState<any>(getCompetenceTree)

  i18n.on('languageChanged', () => {
    setCompetenceTree([...getCompetenceTree])
  })

  const dispatcher = useAppDispatch()

  const fileUploadRef = useRef<FileUpload>(null)

  const reactHookFormMethods = useForm({
    reValidateMode: 'onChange',
    mode: 'onSubmit',
    defaultValues: props.observation
      ? props.observation
      : { ...defaultObservation },
    shouldFocusError: false,
  })

  useEffect(() => {
    const obs = props?.observation
    if (!obs) {
      return
    }
    if (obs?.id_sub_competence) {
      setSubCompetenceDbValue(obs.id_sub_competence)
    }
    if (obs?.id_competence_detail) {
      setSubCompetenceDetailDbValue(obs.id_competence_detail)
    }
    const competenceNode = findCompetenceNodeByObservation(obs)

    if (competenceNode?.key) {
      const subCompetenceKey: string = competenceNode.key.substring(0, 4) + 0
      const competenceKey = subCompetenceKey.substring(0, 1)

      setExpandedKeys({
        '0': true,
        [competenceKey]: true,
        [subCompetenceKey]: true,
      })
      setSelectedCompetenceKey(competenceNode.key)
    }
  }, [props.observation])

  const ratings = educationReportRating()

  const onNodeSelect = (node: any): void => {
    if (node.node.key === '0') {
      return
    }
    reactHookFormMethods.clearErrors('id_sub_competence')

    const competenceKey = node?.node?.key as string
    console.log('selected node:' + node.node.label)
    //const firstDigit = Number.parseInt(competenceKey.substring(0,1))

    if (competenceKey.length === 5) {
      const lastDigit = Number.parseInt(competenceKey.substring(4, 5))
      if (lastDigit !== 0) {
        setSubCompetenceDetailDbValue(node.node.dbId)
        const parentKey = node.node.key.substring(0, 4) + '0'
        const parentNode = findCompetenceNodeByKey(parentKey)
        if (parentNode) {
          console.log('selected parent node:' + parentNode.label)
          setSubCompetenceDbValue(parentNode.dbId)
        }
      } else {
        // no detail selected, just the sub competence
        setSubCompetenceDbValue(node.node.dbId)
      }
    }
  }

  const onNodeUnselect = (): void => {
    setSubCompetenceDbValue(0)
    setSubCompetenceDetailDbValue(0)
  }

  const resetAll = (): void => {
    messages.current?.clear()
    reactHookFormMethods.reset()
    setSelectedCompetenceKey({})
    setSubCompetenceDbValue(0)
    setSubCompetenceDetailDbValue(0)
    fileUploadRef.current?.clear()
  }

  const resetForm = (): void => {
    reactHookFormMethods.setValue('rating', 0)
    reactHookFormMethods.setValue('reason', '')
    reactHookFormMethods.clearErrors()
  }

  // we need to have a valid file name for the file path (url)
  const makeFileNameValid = (name: string): string => {
    return name.replace(/[^a-zA-Z0-9-_.]/g, '')
  }

  const uploadFilesToStore = async (
    id: number,
    filesToUpload: any[],
    uploadUrls: { filename: string; url: string }[]
  ): Promise<Observation> => {
    const errorArray: { filename: string; error: Error }[] = []
    console.log('Starting to upload files to the storage.')

    const uploadFile = async (ob: { filename: string; url: string }) => {
      const fileToUpload = filesToUpload.find(
        (file: any) => makeFileNameValid(file.name) === ob.filename
      )

      await handleRequest(
        ob.url,
        RequestMethod.UPLOAD,
        fileToUpload,
        makeFileNameValid(fileToUpload.name),
        true
      ).catch((error: Error) => {
        errorArray.push({ filename: fileToUpload.name, error: error })
      })
    }

    for (const fileParams of uploadUrls) {
      await uploadFile(fileParams)
    }

    if (errorArray?.length > 0) {
      console.error(
        'One or more files failed upload: ',
        JSON.stringify(errorArray)
      )
    } else {
      console.log('Successfully finished uploading files to the storage.')
    }

    // get the Observation with the added references (need to get them for the file ID)
    const updatedObservation = await handleRequest<Observation>(
      API_OBSERVATION(id),
      RequestMethod.GET
    )
    if (updatedObservation.data) {
      return Promise.resolve(updatedObservation.data)
    }
    return Promise.reject('Could not get Observation.')
  }

  const addFiles = async (
    observationId: number,
    observation: Observation
  ): Promise<Observation> => {
    const filesToUpload = fileUploadRef.current?.getFiles()
    if (filesToUpload && filesToUpload.length > 0) {
      console.log('Files have been added to the report.')
      const fileNames: string[] = []
      filesToUpload.forEach((file: any) => {
        let validPathName = makeFileNameValid(file.name)
        fileNames.push(validPathName)
      })
      const sendBody = { filename: fileNames }
      const uploadUrls = await handleRequest<
        { filename: string; url: string }[]
      >(API_OBSERVATION_UPLOAD(observationId), RequestMethod.POST, sendBody)
      if (!uploadUrls?.data || uploadUrls.error) {
        console.warn('No upload urls returned, an error occurred')
        return Promise.reject(false)
      }
      const observationPromise = await uploadFilesToStore(
        observationId,
        filesToUpload,
        uploadUrls.data
      )
      if (observationPromise) {
        fileUploadRef.current?.clear()
        return Promise.resolve(observationPromise)
      }
      return Promise.reject('Error during upload happened.')
    }
    // no file needs to be added, simply returning the Observation
    return Promise.resolve(observation)
  }

  const update = (id: number, body: Observation): void => {
    messages.current?.clear()
    body.observation_id = id
    handleRequest<Observation>(API_OBSERVATION(id), RequestMethod.PUT, body)
      .then((result: State<Observation>) => {
        console.log(result.data)
        const data = result?.data
        if (!data) {
          console.warn('Observation returned from db seems empty ?!')
          return
        }
        addFiles(id, data)
          .then((observation: Observation) => {
            //dispatcher(updateObservation(data))
            dispatcher(observationUpdated(observation))
            props.handleEvent()

            messages?.current?.show({
              severity: 'success',
              closable: true,
              sticky: false,
              detail: t<string>('observation.success_message_update'),
              life: 5000,
            })
          })
          .catch((error: any) => {
            console.warn(
              'Error caught trying to update a file to the existing Observation.',
              error
            )
          })
      })
      .catch((error: any) => {
        console.error(
          'Error caught trying to update the existing Observation.',
          error
        )
      })
  }

  const add = async (body: Observation) => {
    messages.current?.clear()
    const result = await handleRequest<Observation>(
      API_OBSERVATIONS(),
      RequestMethod.POST,
      body
    )

    const data = result.data
    const obsId = result?.data?.observation_id
    if (!data || !obsId) {
      console.warn(
        'Data returned from the server did not include the Observation ID' +
          ' after trying to add the Observation.'
      )
      return
    }
    const observation = await addFiles(obsId, data).catch((error: any) => {
      console.error(error)
      messages?.current?.show({
        severity: 'error',
        closable: true,
        sticky: true,
        detail: t<string>('observation.failed_message'),
        life: 12000,
      })
      return
    })
    if (!observation) {
      console.error('Error on uploading the observation.')
      messages?.current?.show({
        severity: 'error',
        closable: true,
        sticky: true,
        detail: t<string>('observation.failed_message'),
        life: 12000,
      })
      return
    }

    dispatcher(observationAdded(observation))

    messages?.current?.show({
      severity: 'success',
      closable: true,
      sticky: false,
      detail: t<string>('observation.success_message'),
      life: 5000,
    })

    resetForm()
  }

  const downloadAll = async () => {
    const files = reactHookFormMethods.getValues('observation_file')
    const id = reactHookFormMethods.getValues('observation_id')
    if (!id || !files?.length) {
      return
    }
    console.log('[ObservationEntry] Found files to download')
    const signedUrlResult = await handleRequest<SignedUrlResponse[]>(
      API_OBSERVATION_DOWNLOAD(id),
      RequestMethod.GET
    )

    signedUrlResult?.data?.forEach((signedUrl: SignedUrlResponse) => {
      const url = signedUrl?.url
      if (url) {
        const newWindow: Window | null = window.open(url, '_blank')
        if (newWindow) {
          newWindow.location = url
          newWindow.opener = null
          return
        } else {
          console.warn('Popup blocked!')
          setShowPopupBlocked(true)
        }
      } else {
        console.warn(
          `[Observation Entry] Failed to download file by signed url ${url}`
        )
      }
    })
  }

  const onSubmit = (data: Observation): void => {
    messages.current?.clear()
    let hasError = false
    const idApprentice = reactHookFormMethods.getValues('id_apprentice')
    if (!idApprentice) {
      console.warn('Person needs to be selected.')
      const error: FieldError = {
        type: 'required',
        message: `${t<string>('core.apprentice.neutral')} ${t<string>(
          'core.form.required_text'
        )}`,
      }
      reactHookFormMethods.setError('id_apprentice', error)
      hasError = true
    }

    const rating = reactHookFormMethods.getValues('rating')
    if (!rating || rating === 0) {
      console.warn('Rating needs to be set.')
      const error: FieldError = {
        type: 'required',
        message: `${t<string>('observation.rating')} ${t<string>(
          'core.form.required_text'
        )}`,
      }
      reactHookFormMethods.setError('rating', error)
      hasError = true
    }

    const reason = reactHookFormMethods.getValues('reason')
    if (reason && reason.length > 500) {
      console.warn('Reason is too long')
      const error: FieldError = {
        type: 'maxLength',
        message: t<string>('core.max_length_exceeded', {
          actualLength: reason?.length,
          maxLength: 500,
        }),
      }
      reactHookFormMethods.setError('reason', error)
      hasError = true
    }

    if (subCompetenceDbValue === 0) {
      console.warn('Sub-Competence needs to be set.')
      const error: FieldError = {
        type: 'required',
        message: t<string>('observation.competence_detail'),
      }
      reactHookFormMethods.setError('id_sub_competence', error)
      hasError = true
    }

    if (hasError) {
      console.log('form has errors, not saving')
      messages.current?.replace({
        severity: 'error',
        closable: true,
        life: 5000,
        detail: t<string>('observation.error_message'),
      })
      return
    }

    const body: Observation = {
      id_apprentice: idApprentice,
      rating: rating,
      id_sub_competence: subCompetenceDbValue,
      id_competence_detail:
        subCompetenceDetailDbValue > 0 ? subCompetenceDetailDbValue : undefined,
      reason: trimSpaces(reason),
      hidden: 0,
    }

    const observationId = data.observation_id
    if (observationId) {
      update(observationId, body)
    } else {
      add(body)
    }
  }

  function handleValidationFail(error: any) {
    console.warn('validation failed', error)
  }

  const onTemplateRemove = (file: any, callback: any) => {
    //setTotalSize(totalSize - file.size);
    callback()
  }
  const itemTemplate = uploadFileTemplate(onTemplateRemove)

  const nodeTemplate = (node: TreeNode, options: any) => {
    if (!node.label) {
      return <>???</>
    }

    return (
      <span className={options.className}>
        <>{t<string>(node.label)}</>
      </span>
    )
  }

  const emptyTemplate = (): JSX.Element => {
    return (
      <div
        onDragEnter={() => {
          setValidDropArea(true)
        }}
        onDragLeave={() => {
          setValidDropArea(false)
        }}
        className={`flex align-items-center pl-1 pr-1 pt-1 mt-1 pb-1 mb-1 button-height w-full
                    ${validDropArea ? ' drag-hover ' : ''}`}
        style={{
          background: 'transparent',
          opacity: '0.5',
        }}
      >
        <span className="fa-solid fa-cloud-arrow-up icon-blue mr-2 fa-xl" />{' '}
        <span style={{ fontSize: '1em' }} className="w-full secondary-text">
          {t<string>('core.drag_and_drop')}
        </span>
      </div>
    )
  }

  const selectButtonOptionTemplate = (
    option: any,
    selectedValue: number | undefined
  ) => {
    let classVal = ''

    switch (option.value) {
      case 4:
        classVal = 'border-bottom-D'
        break
      case 3:
        classVal = 'border-bottom-C'
        break
      case 2:
        classVal = 'border-bottom-B'
        break
      case 1:
        classVal = 'border-bottom-A'
    }
    if (selectedValue === option.value) {
      classVal = ''
    }

    return (
      <span
        className={' w-full align-items-center justify-content-center mb-2'}
      >
        <span
          className={classVal + ' justify-content-center align-items-center'}
        >
          <span className={' justify-content-center align-items-center'}>
            {selectedValue === option.value ? (
              <b
                style={{
                  fontWeight: 600,
                  borderBottom: '4px solid white',
                }}
              >
                {option.label}
              </b>
            ) : (
              <>{option.label}</>
            )}
          </span>
        </span>
      </span>
    )
  }

  return (
    <>
      <FormProvider {...reactHookFormMethods}>
        <form onSubmit={reactHookFormMethods.handleSubmit(onSubmit)}>
          <div className="p-fluid formgrid grid">
            <div className="field col-12 md:col-6">
              <label
                htmlFor={'id_sub_competence'}
                className={classNames(
                  {
                    'p-error':
                      reactHookFormMethods.formState.errors.id_sub_competence,
                  },
                  ' mb-0'
                )}
              >
                {t<string>('observation.competence_fields')}
              </label>
              {competenceTree && (
                <Controller
                  name="id_sub_competence"
                  control={reactHookFormMethods.control}
                  render={({ fieldState }) => (
                    <Tree
                      id={'id_sub_competence'}
                      filterPlaceholder={t<string>('observation.search')}
                      value={competenceTree}
                      style={{ border: '1px solid #fff' }}
                      nodeTemplate={nodeTemplate}
                      //filter
                      selectionMode="single"
                      selectionKeys={selectedCompetence}
                      className={classNames({ 'p-invalid': fieldState.error })}
                      onSelectionChange={(e) =>
                        setSelectedCompetenceKey(e.value)
                      }
                      onSelect={onNodeSelect}
                      onUnselect={onNodeUnselect}
                      expandedKeys={expandedKeys}
                      onToggle={(e) => setExpandedKeys(e.value)}
                    />
                  )}
                />
              )}
              {getFormErrorMessage(
                reactHookFormMethods.formState.errors,
                'id_sub_competence'
              )}
            </div>
            {/*<Button type="button" icon="fas fa-expand-alt" onClick={expandAll}*/}
            {/*        className="mr-2 p-button-text icon-orange mb-2" />*/}
            {/*<Button type="button" icon="fas fa-compress-alt" onClick={collapseAll}*/}
            {/*        className="mr-2 p-button-text icon-orange mb-2" />*/}
            <div className="md:col-6 col-12">
              <ApprenticeDropdown />
              <div className={'pt-2 mt-2'}>
                <label
                  htmlFor="rating"
                  className={classNames({
                    'p-error': reactHookFormMethods.formState.errors.rating,
                  })}
                >
                  {t<string>('observation.rating')}
                </label>
                <Controller
                  name="rating"
                  control={reactHookFormMethods.control}
                  rules={{
                    required: getRequiredFormMessage('observation.rating'),
                  }}
                  render={({ field, fieldState }) => (
                    <SelectButton
                      id={field.name}
                      {...field}
                      tooltip={t<string>('core.rating.explanation')}
                      itemTemplate={(option) =>
                        selectButtonOptionTemplate(option, field.value)
                      }
                      tooltipOptions={{
                        appendTo: document.body,
                        position: 'top',
                      }}
                      options={ratings}
                      className={classNames(
                        { 'p-invalid': fieldState.error },
                        'w-full mb-2'
                      )}
                    />
                  )}
                />
                {getFormErrorMessage(
                  reactHookFormMethods.formState.errors,
                  'rating'
                )}
              </div>
              <div className={'pt-2'}>
                <label
                  htmlFor="reason"
                  className={classNames({
                    'p-error': reactHookFormMethods.formState.errors.reason,
                  })}
                >
                  {t<string>('observation.reason')}
                  <span className={'small-text secondary-text'}>
                    {t<string>('core.optional')}
                  </span>
                </label>
                <Controller
                  name="reason"
                  control={reactHookFormMethods.control}
                  render={({ field, fieldState }) => (
                    <InputTextarea
                      id={field.name}
                      {...field}
                      className={classNames(
                        { 'p-invalid': fieldState.error },
                        'w-full mb-2'
                      )}
                      rows={2}
                      autoResize={true}
                    />
                  )}
                />
                {getFormErrorMessage(
                  reactHookFormMethods.formState.errors,
                  'reason'
                )}
              </div>

              <div>
                <ObservationFileList observation={props.observation} />
                {props?.observation?.observation_file &&
                  props.observation.observation_file.length > 0 && (
                    <Button
                      className={'mb-2 p-button-outlined'}
                      label={t<string>('core.download_all_files')}
                      type={'button'}
                      onClick={() => downloadAll()}
                    />
                  )}

                <label className={'mt-4 '} htmlFor={'observationFiles[]'}>
                  {t<string>('observation.file_upload')}{' '}
                  <span className={'small-text secondary-text'}>
                    {t<string>('core.optional')}
                  </span>
                </label>
                <FileUpload
                  ref={fileUploadRef}
                  name="observationFiles[]"
                  customUpload
                  className={'mb-2'}
                  headerTemplate={headerTemplate}
                  chooseOptions={chooseOptions}
                  uploadOptions={uploadOptions}
                  cancelOptions={cancelOptions}
                  emptyTemplate={emptyTemplate}
                  itemTemplate={itemTemplate}
                  multiple
                  onValidationFail={handleValidationFail}
                  accept={
                    '.jpg,.jpeg,.png,.gif,.webP,.svg,.tiff,' +
                    '.log,.txt,' +
                    '.pdf,.ppt,.pptx,.doc,.docx,.xml,.xslt,.xsl,.csv' +
                    '.md,.html,.htm,.json,.odt,.rtf,.url'
                  }
                  maxFileSize={10000000}
                  contentStyle={{
                    backgroundColor: validDropArea ? '#ADCBBF' : 'transparent',
                    transition: '1s ease',
                    border: '1px dashed  rgb(197, 198, 200)',
                    borderRadius: '6px',
                  }}
                />
                {getFormErrorMessage(
                  reactHookFormMethods.formState.errors,
                  'file_upload'
                )}
              </div>

              <div className="formgroup flex pt-2 gap-2 pb-4">
                <Button
                  label={t('core.reset')}
                  type={'button'}
                  onClick={() => resetAll()}
                  className={'col p-button-outlined pt-2 pb-2'}
                />
                <Button
                  type="submit"
                  label={
                    props.observation
                      ? t('observation.edit')
                      : t('observation.save')
                  }
                  className="col p-button-primary pt-2 pb-2"
                />
              </div>
              <Messages ref={messages} />
            </div>
          </div>
        </form>
      </FormProvider>
      <AllowPopupDialog
        closeDialog={() => setShowPopupBlocked(false)}
        showDialog={showPopupBlocked}
      />
      <Toast ref={toast} position={'bottom-right'} />
    </>
  )
}